/**
 * One card per order status: count, share of the orders and (optionally) their value.
 */
import { Box, Card, CardContent, LinearProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ORDER_STATUS_CONFIG, type OrderStatus } from '../../types/order';
import { formatCurrency, formatNumber } from '../../utils/format';

const percent = (part: number, whole: number) => (whole ? Math.round((part * 100) / whole) : 0);

export const StatusBreakdownCards = ({
  items,
}: {
  items: Array<{ status: string; count: number; total_amount?: number }>;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const total = items.reduce((sum, item) => sum + item.count, 0);

  const colorOf = (status: string) => {
    const name = ORDER_STATUS_CONFIG[status as OrderStatus]?.color ?? 'default';
    return name === 'default' ? theme.palette.grey[500] : theme.palette[name].main;
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t('reports.sections.orderStatusBreakdown')}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
        {items.map((item) => {
          const color = colorOf(item.status);
          const share = percent(item.count, total);
          return (
            <Card key={item.status} sx={{ borderTop: `4px solid ${color}` }}>
              <CardContent>
                <Typography variant="body2" fontWeight={600} sx={{ color }}>
                  {t(`components.status.${item.status}`, { defaultValue: item.status })}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1 }}>
                  <Typography variant="h4" fontWeight={700}>
                    {formatNumber(item.count)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('reports.breakdown.ofOrders', { percent: share })}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={share}
                  sx={{ my: 1.5, height: 6, borderRadius: 3, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: color } }}
                />
                {item.total_amount !== undefined && (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      {t('reports.breakdown.ordersValue')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatCurrency(item.total_amount)}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};
